<template>
  <base-wrapper :loading="loading">
    <div class="space-y-6">
      <OrderValidPromoAlert :order-id="this.$route.params.id" />
      <payment-order-detail-card :order="order" />

      <template v-if="pageLoaded">
        <create-payment-form
          v-if="needCreatePayment"
          :min-amount="order.data.attributes.grand_total_price"
          :paid="paidAmount"
          :filter-reserved="false"
          :filter-payment-method-type="{
            'filter[not-name]': 'Saldo Stockist'
          }"
          @save="onCreatePayment"
        />
        
        <payment-list
          :origin-office-id="order.data.attributes.origin_office_id"
          :origin-office-type="order.data.attributes.origin_office_type"
          :order-id="order.data.id"
          :order-code="`${order.data.attributes.origin_code}/${order.data.attributes.destination_code}`"
          :payments="payments"
          :order-price="order.data.attributes.grand_total_price"
          :order-paid="order.data.attributes.is_paid"
          editable
          order-valid
          :payment-method-request-params="
            order.data.attributes.origin_office_id !== me.office_id
              ? {
                  'filter[payment_method_type_uuid_in]':
                    allowedPaymentMethodsId,
                }
              : {}
          "
          :create-payment-form-props="{
            filterReserved: false,
            stockistBalance: this.getSingleIncluded(
              order,
              order.data.relationships['destination-office'].data.id
            ).attributes.order_balance_available
          }"
          with-total-payment-amount
          with-total-payment-remainder
          :payment-amount="paidAmount"
          :acc-payment-reason="order.data.attributes.acc_payment_reason"
          @reload="onReload"
          @verified="onVerifiedPayment"
        />

        <div
          :class="[
            'flex',
            order.data.attributes.is_valid_for_packing
              ? 'justify-end'
              : 'justify-between',
          ]"
        >
          <base-button
            v-if="!order.data.attributes.is_valid_for_packing"
            color="white"
            @click="visibleDeleteConfirm = true"
          >
            <Icon icon="heroicons:x-mark-solid" />
            Batalkan Faktur Penjualan
          </base-button>
          <div class="flex items-center gap-2">
            <base-button
              v-if="accAtasanFormShown"
              @click="visibleAccAtasanConfirmation = true"
            >
              <Icon icon="heroicons:document-solid" />
              Acc Atasan
            </base-button>
            <base-button
              v-if="order.data.attributes.is_paid"
              @click="onPrintInvoice"
            >
              <Icon icon="heroicons:printer-solid" />
              Cetak Invoice
            </base-button>
            <base-button :to="{ name: 'pembayaran.ke-stockist' }" color="white">
              <Icon icon="heroicons:arrow-uturn-left-solid" />
              Kembali
            </base-button>
          </div>
        </div>
      </template>
    </div>

    <base-confirm
      icon-type="badge"
      message="Sudah ada faktur pembayaran. Anda yakin ingin membatalkan?"
      :visible="visibleDeleteConfirm"
      :disabled="!deleteConfirmMessage"
      @close="visibleDeleteConfirm = false"
      @confirmed="onConfirmDelete"
    >
      <template #content>
        <base-input placeholder="Alasan batal faktur penjualan" tag="textarea" :shadow="false" fullwidth rows="4" v-model="deleteConfirmMessage" />
      </template>
    </base-confirm>

    <vue-html2pdf
      :show-layout="false"
      :float-layout="true"
      :preview-modal="true"
      :enable-download="true"
      :paginate-elements-by-height="5000"
      :filename="filename"
      :pdf-quality="2"
      :manual-pagination="false"
      pdf-format="a4"
      pdf-orientation="portrait"
      pdf-content-width="210mm"
      ref="printInvoince"
    >
      <print-invoice-pdf
        slot="pdf-content"
        :order="order.data"
        :payment="payments.data[0]"
        :payment-methods="printInvoice.paymentMethods"
        :destination-office="orderDestinationOffice"
        :origin-office="orderOriginOffice"
        :origin-address="orderOriginOfficeAddress"
      />
    </vue-html2pdf>

    <acc-atasan-confirmation
      :visible="visibleAccAtasanConfirmation"
      :order-id="order.data.id"
      @confirmed="$router.push({ name: 'pembayaran.ke-stockist' })"
      @close="visibleAccAtasanConfirmation = false"
    />
  </base-wrapper>
</template>

<script>
import { requestMixin } from '@/mixins/request/request';
import PaymentOrderDetailCard from '@/components/payment/payment-order-detail-card.vue';
import CreatePaymentForm from '@/components/payment/create-payment-form.vue';
import PaymentList from '@/components/payment/payment-list.vue';
import BaseConfirm from '@/components/base/BaseConfirm.vue';
import { mapActions } from 'vuex';
import VueHtml2pdf from 'vue-html2pdf';
import PrintInvoicePdf from '@/components/order/print-invoice-pdf.vue';
import AccAtasanConfirmation from '@/components/kasir/acc-atasan-confirmation.vue';
import OrderValidPromoAlert from '../../../../../components/promo/OrderValidPromoAlert.vue';
import { mapGetters } from 'vuex';
import dayjs from 'dayjs';

export default {
  components: {
    PaymentOrderDetailCard,
    CreatePaymentForm,
    PaymentList,
    BaseConfirm,
    VueHtml2pdf,
    PrintInvoicePdf,
    AccAtasanConfirmation,
    OrderValidPromoAlert,
  },
  mixins: [requestMixin],
  data() {
    return {
      accAtasanReason: null,
      deleteConfirmMessage: null,
      order: {
        data: {
          attributes: {},
          relationships: {
            'destination-office': {
              data: {
                id: null,
              },
            },
            'origin-office': {
              data: {
                id: null,
              },
            },
          },
        },
        included: [],
      },
      payments: {
        data: [],
      },
      printInvoice: {
        paymentMethods: [],
      },
      loadingOrder: true,
      loadingCreatePayment: false,
      loadingPayments: false,
      loadingCancel: false,
      loadingPrintInvoice: false,
      loadingTransferBankMethodId: false,
      pageLoaded: false,
      allowedPaymentMethodsId: '',
      visibleDeleteConfirm: false,
      visibleAccAtasanConfirmation: false,
    };
  },
  computed: {
    ...mapGetters({
      me: 'auth/getUser',
    }),
    filename() {
      const datetime = dayjs(this.order.data.attributes.createdAt).format('YYYYMMDD')
      const type = 'invoice'
      const code = `${this.order.data.attributes.origin_code}_${this.order.data.attributes.destination_code}`
      const user = this.me.office_code

      return [datetime, type, code, user].join('-')
    },
    loading() {
      return (
        this.loadingOrder ||
        this.loadingCreatePayment ||
        this.loadingPayments ||
        this.loadingCancel ||
        this.loadingPrintInvoice ||
        this.loadingTransferBankMethodId
      );
    },
    orderDestinationOffice() {
      return this.getSingleIncluded(
        this.order,
        this.order.data.relationships['destination-office'].data.id
      );
    },
    orderOriginOffice() {
      return this.getSingleIncluded(
        this.order,
        this.order.data.relationships['origin-office'].data.id
      );
    },
    orderOriginOfficeAddress() {
      const originOffice = this.orderOriginOffice
      
      if (!originOffice) {
        return null
      }

      const addresses = this.getIncludedByType(this.order, 'addresses', originOffice)

      if (!addresses.length) {
        return null
      }

      const defaultAddress = addresses.find(address => address.attributes.is_default)

      if (!defaultAddress) {
        return addresses[0]
      }

      return defaultAddress
    },
    needCreatePayment() {
      if (
        this.order.data.attributes.origin_office_type === 'kantor_pelayanan'
      ) {
        return false;
      }

      if (!this.payments.data.length) {
        return true;
      }

      if (
        this.payments.data.some((payment) => !payment.attributes.is_verified)
      ) {
        return false;
      }

      if (!this.order.data.attributes.is_paid) {
        return true;
      }

      return false;
    },
    paidAmount() {
      return this.payments.data.reduce(
        (total, payment) => total + payment.attributes.cashier_total_amount,
        0
      );
    },
    accAtasanFormShown() {
      return (
        this.needCreatePayment && !this.order.data.attributes.acc_payment_reason
      );
    },
  },
  methods: {
    ...mapActions({
      createAlert: 'alert/createAlert',
    }),
    async createPaymentMethods(payment, paymentMethods) {
      const requests = paymentMethods.map((paymentMethod) => {
        return this.request(`/api/v1/payment-methods`, {
          method: 'post',
          data: {
            data: {
              type: 'payment-methods',
              attributes: {
                cashier_payment_amount: Number(
                  paymentMethod.amount.replace(/\D/gi, '')
                ),
                payment_amount: 0,
                receipt_number: paymentMethod.receipt_number,
              },
              relationships: {
                payment: {
                  data: {
                    type: 'payments',
                    id: payment.data.id,
                  },
                },
                  'payment-method-type': {
                    data: {
                      type: 'payment-method-types',
                      id: paymentMethod.paymentMethodTypeId,
                    },
                  },
                ...(paymentMethod.officeBankId
                  ? {
                      'office-bank': {
                        data: {
                          type: 'office-banks',
                          id: paymentMethod.officeBankId,
                        },
                      },
                    }
                  : {}),
              },
            },
          },
        });
      });

      await Promise.all(requests);
    },
    async loadTransferBankMethodId() {
      this.loadingTransferBankMethodId = true;

      const [res, error] = await this.request(`/api/v1/payment-method-types`, {
        params: {
          'filter[name_in]': 'Transfer Bank,EDC',
        },
      });

      if (!error) {
        this.allowedPaymentMethodsId = res.data
          .map((method) => method.id)
          .join(',');
      }

      this.loadingTransferBankMethodId = false;
    },
    async loadOrder() {
      this.loadingOrder = true;

      const orderId = this.$route.params.id;

      const [res, error] = await this.request(`/api/v1/orders/${orderId}`, {
        params: {
          'fields[orders]':
            'origin_office_id,notes,paid_by,origin_code,destination_code,reference_code,origin_warehouse_code,area_code,grand_total_price,order_shipment,is_paid,is_valid_for_packing,destination-office,origin-office,acc_payment_reason,cashier_payment_amount,payment_amount',
          'fields[offices]':
            'office_type,code,name,temp_code,is_active,order_balance_available,full_address,addresses,phone',
          'fields[addresses]': 'city_name',
          include: 'destination-office,origin-office.addresses',
        },
      });

      if (!error) {
        this.order = res;
      }

      this.loadingOrder = false;
    },
    async loadPayments() {
      this.loadingPayments = true;

      const orderId = this.$route.params.id;

      const [res, error] = await this.request(
        `/api/v1/orders/${orderId}/payments`,
        {
          params: {
            'fields[payments]':
              'is_first_payment,is_verified,total_amount,cashier_total_amount,code,destination_office_code,origin_warehouse_code,state,createdAt,verified_by',
          },
        }
      );

      if (!error) {
        this.payments = res;
      }

      this.loadingPayments = false;
    },
    async onReload() {
      if (
        this.order.data.attributes.origin_office_type === 'kantor_pelayanan'
      ) {
        await Promise.all([this.loadOrder(), this.loadPayments()]);
      } else {
        await Promise.all([this.loadPayments()]);
      }
    },
    async onConfirmDelete() {
      this.loadingCancel = true;

      const [, error] = await this.request(
        `/api/v1/orders/${this.order.data.id}/-actions/cancelPayment`,
        {
          method: 'patch',
          data: {
            cancel_payment_description: this.deleteConfirmMessage,
          },
        }
      );

      if (!error) {
        this.$router.push({ name: 'pembayaran.ke-stockist' });
      }

      this.loadingCancel = false;
    },
    async onCreatePayment(paymentMethods) {
      this.loadingCreatePayment = true;

      const [payment, error] = await this.request(`/api/v1/payments`, {
        method: 'post',
        data: {
          data: {
            type: 'payments',
            relationships: {
              order: {
                data: {
                  type: 'orders',
                  id: this.order.data.id,
                },
              },
            },
          },
        },
      });

      if (!error) {
        await this.createPaymentMethods(payment, paymentMethods);

        this.loadPayments();
      }

      this.loadingCreatePayment = false;
    },
    async onVerifiedPayment() {
      await Promise.all([this.loadOrder(), this.loadPayments()]);

      if (this.order.data.attributes.is_paid) {
        await this.onPrintInvoice();

        const waitForEl = setInterval(() => {
          const el = document.querySelector(
            'div.vue-html2pdf > section.pdf-preview > button'
          );

          if (el) {
            el.onclick = () =>
              this.$router.push({ name: 'pembayaran.ke-stockist' });
            clearInterval(waitForEl);
          }
        }, 100);
      }
    },
    async onPrintInvoice() {
      this.loadingPrintInvoice = true;

      const [res, err] = await this.request(
        `/api/v1/payments/${this.payments.data[0].id}/payment-methods`,
        {
          params: {
            'filter[canceled]': false,
            'fields[payment-methods]':
              'payment_method_type,cashier_payment_amount,payment_amount,is_used',
          },
        }
      );

      if (!err) {
        this.printInvoice.paymentMethods = res.data.filter(
          (item) => item.attributes.is_used
        );

        this.$refs.printInvoince.generatePdf();
      }

      this.loadingPrintInvoice = false;
    },
  },
  async created() {
    await this.loadTransferBankMethodId();
    await Promise.all([this.loadOrder(), this.loadPayments()]);

    this.pageLoaded = true;
  },
};
</script>
